import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Download, Printer, Loader2 } from "lucide-react";
import { http, formatApiError, pdfUrl } from "@/lib/api";
import { SiteFooter } from "@/components/Brand";
import { SiteHeader } from "@/components/SiteHeader";

export const parseDoc = (text) => {
  const blocks = [];
  text.split("\n").forEach((raw) => {
    const line = raw.trim();
    if (!line) return;
    const id = `b-${blocks.length}`;
    if (line.startsWith("## ")) blocks.push({ type: "h", text: line.slice(3), id: `sez-${blocks.length}` });
    else if (line.startsWith("- ")) blocks.push({ type: "li", text: line.slice(2), id });
    else blocks.push({ type: "p", text: line, id });
  });
  return blocks;
};

export const DocBody = ({ blocks }) => (
  <div className="doc-body space-y-4 text-[15px] leading-[1.8] text-slate-700">
    {blocks.map((b) =>
      b.type === "h" ? (
        <h2 key={b.id} id={b.id} className="scroll-mt-24 pt-4 font-heading text-lg font-bold tracking-tight text-slate-900">{b.text}</h2>
      ) : b.type === "li" ? (
        <p key={b.id} className="relative !mt-1.5 pl-5 before:absolute before:left-1 before:top-[0.75em] before:h-1.5 before:w-1.5 before:rounded-full before:bg-blue-600">{b.text}</p>
      ) : (
        <p key={b.id}>{b.text}</p>
      )
    )}
  </div>
);

export default function DocumentView() {
  const { cf, tipo } = useParams();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    http.get(`/public/condomini/${encodeURIComponent(cf)}/documenti/${tipo}`)
      .then(({ data }) => setDoc(data))
      .catch((e) => setError(formatApiError(e)));
  }, [cf, tipo]);

  const blocks = doc ? parseDoc(doc.contenuto) : [];
  const toc = blocks.filter((b) => b.type === "h");

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <SiteHeader />
      <main className="mx-auto w-full max-w-6xl flex-1 px-6 py-12">
        <Link to={`/informative?q=${cf}`} data-testid="doc-back-link" className="no-print mb-8 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900">
          <ArrowLeft className="h-4 w-4" /> Torna ai documenti
        </Link>
        {error && <p data-testid="doc-error" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">{error}</p>}
        {!doc && !error && <Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" />}
        {doc && (
          <div className="grid gap-10 lg:grid-cols-[240px_1fr]">
            <aside className="no-print lg:sticky lg:top-24 lg:self-start">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Indice</p>
              <nav className="mt-3 space-y-1 border-l border-slate-200" data-testid="doc-toc">
                {toc.map((h) => (
                  <a key={h.id} href={`#${h.id}`} className="-ml-px block border-l-2 border-transparent py-1 pl-3 text-sm text-slate-600 transition-colors hover:border-blue-600 hover:text-slate-900">{h.text}</a>
                ))}
              </nav>
              <div className="mt-8 flex flex-col gap-2">
                <a href={pdfUrl(cf, tipo)} data-testid="doc-download-pdf-button" className="inline-flex items-center justify-center gap-2 rounded-md bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-600">
                  <Download className="h-4 w-4" /> Scarica PDF
                </a>
                <button onClick={() => window.print()} data-testid="doc-print-button" className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:border-slate-900">
                  <Printer className="h-4 w-4" /> Stampa
                </button>
              </div>
            </aside>
            <article className="fade-up rounded-2xl border border-slate-200 bg-white px-6 py-10 shadow-sm sm:px-14" data-testid="doc-content">
              <p className="font-mono text-xs uppercase tracking-wider text-blue-600">{doc.codice}</p>
              <h1 data-testid="doc-title" className="mt-2 font-heading text-3xl font-bold leading-tight tracking-tight text-slate-900 sm:text-4xl">{doc.titolo}</h1>
              <p className="mt-3 text-sm text-slate-500">{doc.condominio.nome} · <span className="font-mono">C.F. {doc.condominio.codice_fiscale}</span></p>
              <div className="my-8 h-px bg-slate-200" />
              <DocBody blocks={blocks} />
            </article>
          </div>
        )}
      </main>
      <div className="no-print"><SiteFooter /></div>
    </div>
  );
}
